// ─────────────────────────────────────────────────────────────
// auth.jsx — sign-in and access gate for the SB Training Hub (OA Auth Task 09)
// ─────────────────────────────────────────────────────────────
//   booting → signed-out (email → 6-digit code)
//           → checking → allowed  (<App />)
//                      → denied / error ("No access")
// Who gets in is read fresh from the database after every sign-in:
// sbt_caps().use = can_use_sb_hubs() = active AND (office staff OR crew).
// Clients and unknown accounts see "No access" and nothing loads. sbt_caps()
// also says whether this person may edit (hub_members sb_training / edit) and
// see backups; the database enforces the same rules on every sbt_* table.

import React, { useEffect, useRef, useState } from "react";
import { supabase } from "./lib/supabase";
import { authReady, signOutHere, watchSharedSession } from "./lib/oaAuth";
import SilverbackTrainingHub from "./App";

const C = { charcoal: "#1C1C1E", charcoalLt: "#3A3A3C", silver: "#A8A8B3", mist: "#8E8E93", slate: "#48484A", bg: "#F5F5F7", line: "#E0E0E5", red: "#B42318" };
const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

function Shell({ children }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, fontFamily: FONT, padding: 20, boxSizing: "border-box" }}>
      <div style={{ width: "100%", maxWidth: 400, background: "#fff", border: `1px solid ${C.line}`, borderRadius: 14, padding: "32px 28px", boxShadow: "0 20px 50px -25px rgba(0,0,0,0.35)" }}>
        <div style={{ fontSize: 10, letterSpacing: 3, color: C.mist, textTransform: "uppercase", marginBottom: 4 }}>Silverback Logistics</div>
        <div style={{ fontSize: 20, fontWeight: "bold", color: C.charcoal, marginBottom: 22 }}>Training Hub</div>
        {children}
      </div>
    </div>
  );
}
const btn = (busy, disabled) => ({
  width: "100%", marginTop: 14, padding: "12px", background: busy || disabled ? C.mist : C.charcoal, color: "#fff",
  border: "none", borderRadius: 8, cursor: busy ? "wait" : disabled ? "not-allowed" : "pointer", fontSize: 15, fontWeight: "bold", fontFamily: FONT,
});
const input = { width: "100%", boxSizing: "border-box", padding: "12px 14px", borderRadius: 8, border: `2px solid ${C.line}`, fontSize: 16, fontFamily: FONT, outline: "none" };

function LoginScreen() {
  const [phase, setPhase] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function send(e) {
    e?.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true); setErr("");
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: { shouldCreateUser: false },   // invite-only
      });
      if (error) throw error;
      setPhase("code");
    } catch (e2) {
      setErr(/signups? not allowed/i.test(e2?.message || "") ? "Email not recognised. Ask Corey or David to get access." : (e2?.message || "Could not send the code. Try again."));
    } finally { setBusy(false); }
  }

  async function verify(e) {
    e?.preventDefault();
    if (code.trim().length < 6 || busy) return;
    setBusy(true); setErr("");
    try {
      const { error } = await supabase.auth.verifyOtp({ email: email.trim().toLowerCase(), token: code.trim(), type: "email" });
      if (error) throw error;
    } catch {
      setErr("That code is invalid or expired. Request a new one.");
    } finally { setBusy(false); }
  }

  return (
    <Shell>
      {phase === "email" ? (
        <form onSubmit={send}>
          <p style={{ color: C.slate, fontSize: 14, lineHeight: 1.5, margin: "0 0 16px" }}>Enter your email. We'll send a 6-digit sign-in code.</p>
          <input type="email" autoFocus autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@silverbacklogistics.ca" disabled={busy} style={input} />
          <button type="submit" disabled={busy || !email.trim()} style={btn(busy, !email.trim())}>{busy ? "Sending…" : "Send sign-in code"}</button>
        </form>
      ) : (
        <form onSubmit={verify}>
          <p style={{ color: C.slate, fontSize: 14, lineHeight: 1.5, margin: "0 0 16px" }}>We sent a 6-digit sign-in code to <strong>{email}</strong>. Enter it below.</p>
          <input autoFocus inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))} placeholder="6-digit code" disabled={busy} style={{ ...input, textAlign: "center", letterSpacing: 6, fontSize: 22 }} />
          <button type="submit" disabled={busy || code.trim().length < 6} style={btn(busy, code.trim().length < 6)}>{busy ? "Verifying…" : "Sign in"}</button>
          <button type="button" onClick={() => { setPhase("email"); setCode(""); setErr(""); }} style={{ width: "100%", marginTop: 10, background: "none", border: "none", color: C.charcoalLt, cursor: "pointer", fontSize: 14, fontFamily: FONT }}>← Use a different email</button>
        </form>
      )}
      {err && <div style={{ marginTop: 12, color: C.red, fontSize: 13, lineHeight: 1.4 }}>{err}</div>}
    </Shell>
  );
}

function NoAccess({ email, failed, onRetry }) {
  return (
    <Shell>
      <h2 style={{ color: C.charcoal, fontSize: 18, margin: "0 0 8px" }}>No access</h2>
      <p style={{ color: C.slate, fontSize: 14, lineHeight: 1.6, margin: "0 0 6px" }}>
        {failed
          ? "We couldn’t check your access just now. Try again in a moment."
          : <>The Training Hub is for the Silverback crew and the Obvious Advantage team. {email ? <strong>{email}</strong> : "This account"} doesn&rsquo;t have access. If that&rsquo;s a mistake, ask Corey or David.</>}
      </p>
      {failed && <button type="button" onClick={onRetry} style={{ ...btn(false, false), background: "#fff", color: C.charcoal, border: `2px solid ${C.charcoal}` }}>Try again</button>}
      <button type="button" onClick={() => signOutHere()} style={btn(false, false)}>Sign out</button>
    </Shell>
  );
}

function Boot() {
  return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, color: C.mist, fontFamily: FONT, fontSize: 14 }}>Loading…</div>;
}

async function checkAccess(session) {
  const { data: caps, error } = await supabase.rpc("sbt_caps");
  if (error) return { status: "error" };
  if (caps?.use !== true) return { status: "denied" };
  // Own row only (app_users RLS); used for "Signed in as …".
  const { data: me } = await supabase.from("app_users").select("id, email, display_name").eq("id", session.user.id).maybeSingle();
  return {
    status: "allowed",
    user: { id: session.user.id, email: me?.email || session.user.email, name: me?.display_name || "" },
    caps: { edit: caps?.edit === true, backups: caps?.backups === true },
  };
}

export default function AuthGate() {
  const [state, setState] = useState({ phase: "booting" });
  const checkedUser = useRef(null);   // user id the current verdict belongs to

  async function run(session) {
    checkedUser.current = session.user.id;
    setState({ phase: "checking" });
    let result;
    try { result = await checkAccess(session); } catch { result = { status: "error" }; }
    if (checkedUser.current !== session.user.id) return;
    setState({ phase: result.status, email: session.user.email || "", user: result.user, caps: result.caps });
  }

  useEffect(() => {
    let cancelled = false, sub = null, unwatch = null;
    // One verdict per user: token refreshes don't re-run it; a new user always does.
    function onSession(session) {
      if (cancelled) return;
      if (!session) { checkedUser.current = null; setState({ phase: "signed-out" }); return; }
      if (checkedUser.current === session.user.id) return;
      run(session);
    }
    // Wait for the one-time localStorage → shared-cookie migration (oaAuth).
    authReady().then(() => {
      if (cancelled) return;
      supabase.auth.getSession()
        .then(({ data }) => onSession(data?.session ?? null))
        .catch(() => { if (!cancelled) setState({ phase: "signed-out" }); });
      ({ data: sub } = supabase.auth.onAuthStateChange((_e, session) => onSession(session)));
      // Another module signed out, or someone else signed in, while away
      // (matters on the shared warehouse tablet).
      unwatch = watchSharedSession(onSession);
    });
    return () => { cancelled = true; sub?.subscription?.unsubscribe?.(); unwatch?.(); };
  }, []);

  if (state.phase === "booting" || state.phase === "checking") return <Boot />;
  if (state.phase === "signed-out") return <LoginScreen />;
  if (state.phase !== "allowed") {
    return (
      <NoAccess
        email={state.email}
        failed={state.phase === "error"}
        onRetry={() => supabase.auth.getSession().then(({ data }) => {
          if (data?.session) run(data.session); else setState({ phase: "signed-out" });
        })}
      />
    );
  }
  return <SilverbackTrainingHub key={state.user.id} user={state.user} caps={state.caps} onSignOut={() => signOutHere()} />;
}
